import React, { useState, useEffect, useContext, createContext } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { Box, Container, CircularProgress } from "@mui/material";
import CssBaseline from "@mui/material/CssBaseline";
import MenuBar from "./components/MenuBar/MenuBar";
import InventoryPage from "./components/InventoryPage/Inventory";
import SalesRecord from "./components/SalesRecordPage/SalesRecord";
import SalesRecordArchive from "./components/SalesArchive/SalesRecordArchive";
import Checkout from "./components/SalesList/Checkout";
import ConfirmationPage from "./components/SalesList/ConfirmationPage";
import SalesList from "./components/SalesList/SalesList";
import ProductDetails from "./components/ProductDetails/ProductDetails";
import Cart from "./components/Cart/Cart";
import GlobalProvider from "./GlobalProvider";
import ShipmentPage from "./components/ShipmentPage/shipment";
import ShippedItems from "./components/ShippedItems/ShippedItems";
import InventoryDetails from "./components/InventoryDetailsPage/InventoryDetails";
import Analytics from "./components/AnalyticsPage/Analytics";
import ParentComponent from "./components/InventoryManagement/ParentComponent";
import WelcomePage from "./components/WelcomePage/WelcomePage";
import Footer from "./components/Footer/Footer";
import ComboOffer from "./components/ComboOffer/ComboOffer";
import ComboOfferDetailsPage from "./components/ComboOffer/ComboOfferDetailsPage";
import AdminIconMenu from "./components/AdminIconMenu/AdminIconMenu";
import HeaderGrid from "./components/Header/HeaderGrid";
import { API_BASE_URL, IMAGE_BASE_URL } from "./config/constants";
import { ToastProvider } from "./utility-v1/ToastProvider";
import GoogleLoginComponent from "./utility-v1/GoogleLoginComponent";
import PrivateRoute from "./PrivateRoute";

const ImageCacheContext = createContext();
export const useImageCache = () => useContext(ImageCacheContext);
const HeaderGridWrapper = ({ user, allItems }) => {
  const location = useLocation();
  const pathArray = location.pathname.split("/").filter(Boolean);

  if (pathArray[0] === "jewels" && pathArray[1]) {
    return (
      <HeaderGrid
        pathArray={pathArray}
        allItems={allItems}
        content=""
        additionalContent={user ? <AdminIconMenu /> : null} // Show only if user is logged in
      />
    );
  }
  return null;
};

function App() {
  const [allItems, setAllItems] = useState([]);
  const [imageCache, setImageCache] = useState(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);

  // Check sessionStorage for user on initial load
  useEffect(() => {
    const storedUser = sessionStorage.getItem("user");
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser)); // Load user from sessionStorage
      } catch (error) {
        console.error("Failed to parse user from sessionStorage:", error);
      }
    } else {
    }
  }, []);
  // Fetch and cache images
  useEffect(() => {
    const fetchAndCacheImages = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/filtered-items`);
        const data = await response.json();

        const cache = new Map();

        // Preload images
        /*    await Promise.all(
          data.map((item) => {
            const img = new Image();
            img.src = `${IMAGE_BASE_URL}${item.image}`;
            return new Promise((resolve, reject) => {
              img.onload = () => {
                cache.set(item.image, img.src);
                resolve();
              };
              img.onerror = reject;
            });
          })
        );
                setImageCache(cache);
*/
        setAllItems(data);
      } catch (error) {
        console.error("Error fetching items or caching images:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAndCacheImages();
  }, []);

  // Show Google login if user is not authenticated

  // Show loading spinner while fetching data
  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Render app after user login and loading complete
  return (
    <ImageCacheContext.Provider value={imageCache}>
      <Router>
        <CssBaseline />
        <GlobalProvider>
          <Container maxWidth="xl">
            <ToastProvider>
              <MenuBar allItems={allItems} />
              <HeaderGridWrapper user={user} allItems={allItems} />

              <Box>
                <Routes>
                  <Route path="/" element={<WelcomePage />} />
                  <Route
                    path="/ammulogin"
                    element={<GoogleLoginComponent setUserParent={setUser} />}
                  />
                  <Route path="/jewels" element={<WelcomePage />} />
                  <Route
                    path="/jewels/list/:category"
                    element={<SalesList allItems={allItems} />}
                  />
                  <Route
                    path="/jewels/list/:category/:id"
                    element={<ProductDetails />}
                  />
                  <Route
                    path="/jewels/checkout"
                    element={<Checkout user={user} allItems={allItems} />}
                  />
                  <Route
                    path="/jewels/confirmation"
                    element={<ConfirmationPage user={user} />}
                  />
                  <Route path="/jewels/bag" element={<Cart  allItems={allItems} />} />
                  <Route path="/jewels/combooffer" element={<ComboOffer />} />
                  <Route
                    path="/jewels/combooffer/:id"
                    element={<ComboOfferDetailsPage />}
                  />
                  {/* 
                  <Route path="/jewels/inventory" element={<InventoryPage />} />
                  <Route path="/sales" element={<SalesRecord />} />
                  <Route path="/jewels/shipment" element={<ShipmentPage />} />
                  <Route path="/jewels/inventory-details" element={<InventoryDetails />} />
                  <Route path="/jewels/analytics" element={<Analytics />} />
                  <Route path="/jewels/admin" element={<ParentComponent />} />
                    */}

                  {/* Commenting out PrivateRoute */}

                  <Route
                    path="/jewels/inventory"
                    element={
                      <PrivateRoute user={user}>
                        <InventoryPage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/jewels/sales"
                    element={
                      <PrivateRoute user={user}>
                        <SalesRecord />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/jewels/salesarchive"
                    element={
                      <PrivateRoute user={user}>
                        <SalesRecordArchive />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/jewels/shipment"
                    element={
                      <PrivateRoute user={user}>
                        <ShipmentPage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/jewels/shipment-completed"
                    element={
                      <PrivateRoute user={user}>
                        <ShippedItems />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/jewels/inventory-details"
                    element={
                      <PrivateRoute user={user}>
                        <InventoryDetails />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/jewels/analytics"
                    element={
                      <PrivateRoute user={user}>
                        <Analytics />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/jewels/admin"
                    element={
                      <PrivateRoute user={user}>
                        <ParentComponent />
                      </PrivateRoute>
                    }
                  />
                </Routes>
              </Box>
            </ToastProvider>
          </Container>
        </GlobalProvider>
      </Router>
    </ImageCacheContext.Provider>
  );
}

export default App;
